import React, { useContext, useEffect, useState } from "react";
import useAxios from "../../../hooks/useAxios";
import TeacherCard from "../../../components/DashboardComponents/Cards/TeacherCard/TeacherCard";
import Header from "../../../components/Header/Header";
import ContentHeader from "../../../components/common/ContentHeader/ContentHeader";
import "./SchoolDashboardPage.css";
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupIcon from '@mui/icons-material/Group';
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SchoolIcon from "@mui/icons-material/School";
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import GroupsIcon from "@mui/icons-material/Groups";

import DashboardIcon from "@mui/icons-material/Dashboard";
import StatCard from "../../../components/DashboardComponents/Cards/StatCard/StatCard";
import ReportCard from "../../../components/Reporting/Cards/ReportCard";
import { useNavigate } from "react-router-dom";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { AuthContext } from "../../../context/AuthContext";

const navLinks = [
];

function SchoolDashboardPage() {
  const axiosInstance = useAxios();
  const navigate = useNavigate();
  const { setStatus } = useContext(AuthContext);
  const [teachers, setTeachers] = useState([]);
  const [schoolSummary, setSchoolSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('Copy ID')

  const fetchTeachers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get("/users/teachers");
      if (response.data.success) setTeachers(response.data.teachers);
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSchoolSummary = async () => {
    try {
      const response = await axiosInstance.get("/school-report/summary");
      setSchoolSummary(response.data);
      setStatus(schoolSummary.user.status);
    } catch (err) {
      console.error("Error fetching school summary:", err);
    }
  };

  useEffect(() => {
    fetchTeachers();
    fetchSchoolSummary();
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(schoolSummary.unique_identifier || "");
    setTitle('Copied!');
  };
  

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <Container className="school-dashboard-container page-content">
        {schoolSummary && (
          <Box className="hero-card">
            <Avatar className="hero-icon">
              <DashboardIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" className="hero-title">
              {schoolSummary.school_name}
            </Typography>
            {/* <Typography className="hero-subtitle">
              Your school ID is: {schoolSummary.unique_identifier}
            </Typography> */}
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                ID: <span className="red-note">{schoolSummary.unique_identifier}</span> 
              </Typography>
              <Tooltip title={title}>
                <IconButton onClick={handleCopy} >
                  <ContentCopyIcon sx={{ color: 'white' }}/>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Summary Section */}
        {schoolSummary && (
          <Grid container spacing={3} className="stats-grid">
            <StatCard
              title="Teachers"
              value={schoolSummary.number_of_teachers}
              icon={<PeopleAltIcon />}
              color="#3f51b5"
            />
            <StatCard
              title="Students"
              value={schoolSummary.number_of_students}
              icon={<GroupsIcon />}
              color="#ff9800"
            />
            <StatCard
              title="Assessments"
              value={schoolSummary.total_quizzes}
              icon={<QuestionAnswerIcon />}
              color="#4caf50"
            />
            <StatCard
              title="Sessions"
              value={schoolSummary.total_sessions}
              icon={<SchoolIcon />}
              color="#f44336"
            />
          </Grid>
        )}

        {/* Teachers Management */}
        <ContentHeader title="Teachers" />
        {loading ? (
          <Box className="loading-container">
            <CircularProgress />
            <Typography>Loading Teachers...</Typography>
          </Box>
        ) : ( 
          <Grid container spacing={2} className="mb-5">
            {teachers.map((teacher) => (
              <Grid item xs={12} sm={6} md={4} key={teacher.id}>
                <TeacherCard teacher={teacher} onActionComplete={fetchTeachers} />
              </Grid>
            ))}
          </Grid>
        )}
        {teachers.length === 0 && (
          <>
            <Typography className="my-5 mx-3">
              No teachers assigned to your school yet
            </Typography>
          </>
        )

        }
        {/* Reporting */}
        <ContentHeader title="Reporting" />
        <div className="reports-grid mb-5">
          {/* Teacher-Specific Reports Card */}
          <ReportCard
              icon={AssessmentIcon}
              title="Teacher-Specific Analysis"
              description="Monitor all Assessments created by a teacher, with all of its related data, like sessions, evaluation, and so on."
              buttonLabel="View Reports"
              onButtonClick={() => navigate('/reporting/teacher-specific', { state: { teachers: teachers } })}
              />
          {/* Quiz-Specific Reports Card */}
          <ReportCard
              icon={AssessmentIcon}
              title="Assessment-Specific Analysis"
              description="Monitor all Sessions for an Assessment, with all of its related data, like users, evaluation, and so on."
              buttonLabel="View Reports"
              onButtonClick={() => navigate('/reporting/quiz-specific')}
              />
        </div>
      </Container>
    </>
  );
}
export default SchoolDashboardPage;